import type { AttendanceRecord } from '../../services/api/attendanceService';
import type { DefectBoardRow } from '../../services/api/defectService';
import type { Task } from '../../services/api/tasksService';
import type { Worker } from '../../services/api/timesheetService';
import { normalizeStatus } from './workerFieldModeUtils';

export type WorkerPeriod = 'month' | 'quarter' | 'custom' | 'all';
export type WorkerAttendanceStatus = 'present' | 'late' | 'absent';

export interface WorkerPerformanceSummary {
  qualityScore: number;
  speedScore: number;
  attendanceScore: number;
  passed: number;
  failed: number;
  defectCount: number;
  aheadOrOnTime: number;
  lateTasks: number;
  presentDays: number;
  lateDays: number;
  absentDays: number;
  hours: number;
}

const dateKey = (value?: string | null): string | null => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const normalizePersonName = (value?: string | null): string =>
  String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ');

export const findAuthenticatedWorker = (
  workers: Worker[],
  firstName?: string,
  lastName?: string
): Worker | null => {
  const target = normalizePersonName(`${firstName || ''} ${lastName || ''}`);
  if (!target) return null;
  return workers.find((worker) => normalizePersonName(worker.name) === target) ?? null;
};

export const periodStart = (period: WorkerPeriod, now = new Date()): string | null => {
  if (period === 'all') return null;
  const months = period === 'month' ? 1 : 3;
  return dateKey(new Date(now.getFullYear(), now.getMonth() - months, now.getDate()).toISOString());
};

export const dateInWorkerPeriod = (
  value: string | null | undefined,
  period: WorkerPeriod,
  now = new Date(),
  customRange?: { from?: string; to?: string }
): boolean => {
  const key = dateKey(value);
  if (!key) return false;
  if (period === 'custom') {
    if (customRange?.from && key < customRange.from) return false;
    if (customRange?.to && key > customRange.to) return false;
    return true;
  }
  const from = periodStart(period, now);
  return !from || key >= from;
};

export const taskResultDate = (task: Task): string | null =>
  task.workerCompletedAt || task.completionDate || task.completedAt || task.updatedAt || null;

export const taskResultDateKey = (task: Task): string | null =>
  dateKey(taskResultDate(task));

export const formatWorkerDateLabel = (value?: string | null): string => {
  if (!value) return '-';
  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day))
      .toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

export const attendanceHours = (record: AttendanceRecord): number => {
  if (!record.scanInAt || !record.scanOutAt) return 0;
  const start = new Date(record.scanInAt).getTime();
  const end = new Date(record.scanOutAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.round(((end - start) / 36e5) * 10) / 10;
};

export const attendanceStatus = (
  record: AttendanceRecord,
  lateAfterHour = 7,
  lateAfterMinute = 15
): WorkerAttendanceStatus => {
  if (/\babsent\b/i.test(record.notes || '') || !record.scanInAt) return 'absent';
  const scanIn = new Date(record.scanInAt);
  if (Number.isNaN(scanIn.getTime())) return 'absent';
  const minutes = scanIn.getHours() * 60 + scanIn.getMinutes();
  return minutes > lateAfterHour * 60 + lateAfterMinute ? 'late' : 'present';
};

const clampScore = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

export const computeWorkerPerformance = (
  tasks: Task[],
  defects: DefectBoardRow[],
  attendance: AttendanceRecord[],
  period: WorkerPeriod,
  now = new Date(),
  customRange?: { from?: string; to?: string }
): WorkerPerformanceSummary => {
  const periodTasks = tasks.filter((task) => dateInWorkerPeriod(taskResultDate(task), period, now, customRange));
  const completed = periodTasks.filter((task) =>
    ['INSPECTION_PENDING', 'INSPECTION_PASSED', 'INSPECTION_FAILED'].includes(normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status))
  );
  const passed = completed.filter((task) => normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status) === 'INSPECTION_PASSED').length;
  const failed = completed.filter((task) => normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status) === 'INSPECTION_FAILED').length;
  const visibleTaskIds = new Set(periodTasks.map((task) => task.id));
  const defectCount = defects.filter((defect) => visibleTaskIds.has(defect.originalTaskId) && dateInWorkerPeriod(defect.createdAt, period, now, customRange)).length;
  const qualityBase = completed.length ? (passed / completed.length) * 100 : 0;
  const qualityScore = completed.length ? clampScore(qualityBase - Math.min(30, defectCount * 5)) : 0;

  const actualHoursForTask = (task: Task) => {
    const workerSeconds = Number(task.workerActualSeconds ?? 0);
    return workerSeconds > 0 ? workerSeconds / 3600 : Number(task.actualHours ?? 0);
  };
  const durationTasks = completed.filter((task) => Number(task.estimatedHours) > 0 && actualHoursForTask(task) > 0);
  const aheadOrOnTime = durationTasks.filter((task) => actualHoursForTask(task) <= Number(task.estimatedHours)).length;
  const lateTasks = durationTasks.length - aheadOrOnTime;
  const speedScore = durationTasks.length ? clampScore((aheadOrOnTime / durationTasks.length) * 100) : 0;

  const periodAttendance = attendance.filter((record) => dateInWorkerPeriod(record.attendanceDate, period, now, customRange));
  const statuses = periodAttendance.map((record) => attendanceStatus(record));
  const presentDays = statuses.filter((status) => status === 'present').length;
  const lateDays = statuses.filter((status) => status === 'late').length;
  const absentDays = statuses.filter((status) => status === 'absent').length;
  const attendanceScore = statuses.length
    ? clampScore(((presentDays + lateDays * 0.5) / statuses.length) * 100)
    : 0;

  return {
    qualityScore,
    speedScore,
    attendanceScore,
    passed,
    failed,
    defectCount,
    aheadOrOnTime,
    lateTasks,
    presentDays,
    lateDays,
    absentDays,
    hours: Math.round(periodAttendance.reduce((sum, record) => sum + attendanceHours(record), 0) * 10) / 10,
  };
};

export const taskSafetyProfile = (task: Task): { hazards: string[]; protection: string[] } => {
  const text = [
    task.title,
    task.description,
    task.taskType,
    (task as any).methodStatement,
    ...(task.specifications || []).map((item: any) => typeof item === 'string' ? item : item?.text || ''),
  ].filter(Boolean).join(' ').toLowerCase();
  const hazards = new Set<string>();
  const protection = new Set<string>(['Hard hat', 'Hi-vis vest', 'Safety boots']);

  if (/height|scaffold|roof|ladder|elevated/.test(text)) {
    hazards.add('Working at height');
    protection.add('Safety harness and inspected anchor');
  }
  if (/electric|conduit|cable|wiring|power/.test(text)) {
    hazards.add('Electrical energy and power tools');
    protection.add('Insulated gloves');
    protection.add('Eye protection');
  }
  if (/rebar|steel|formwork|concrete|block|masonry/.test(text)) {
    hazards.add('Sharp edges, dust, and manual handling');
    protection.add('Work gloves');
    protection.add('Eye protection');
  }
  if (/excavat|trench|foundation|pile/.test(text)) {
    hazards.add('Excavation edges and unstable ground');
    protection.add('Safe access ladder and barricades');
  }
  if (/lift|crane|hoist|rigg/.test(text)) {
    hazards.add('Suspended loads and lifting zone');
    protection.add('Exclusion-zone barriers and signaler');
  }
  if (/cut|grind|saw|drill|dust/.test(text)) {
    hazards.add('Flying particles and dust');
    protection.add('Eye protection');
    protection.add('Dust mask or respirator');
  }
  if (!hazards.size) hazards.add('Manual handling, access, and housekeeping');
  return { hazards: [...hazards], protection: [...protection] };
};
